import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Modal, Badge, Spinner, Alert } from 'react-bootstrap';
import { 
  FaBuilding, 
  FaPlus, 
  FaEdit, 
  FaTrash,
  FaSearch
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import bancosApi from '../../services/api/bancosApi';
import 'react-toastify/dist/ReactToastify.css';

const MantenimientoBancos = () => {
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBanco, setEditingBanco] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    nombre_corto: '',
    activo: true
  });

  useEffect(() => {
    loadBancos();
  }, []);

  const loadBancos = async () => {
    setLoading(true);
    try {
      const response = await bancosApi.getBancos(null); // Obtener todos, activos e inactivos
      if (response.data && response.data.data) {
        setBancos(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar bancos:', error);
      toast.error('Error al cargar bancos');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setEditingBanco(null);
    setFormData({
      codigo: '',
      nombre: '',
      nombre_corto: '',
      activo: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingBanco) {
        await bancosApi.updateBanco(editingBanco.id, formData);
        toast.success('Banco actualizado correctamente');
      } else {
        await bancosApi.createBanco(formData);
        toast.success('Banco creado correctamente');
      }

      setShowModal(false);
      resetForm();
      loadBancos();
    } catch (error) {
      console.error('Error al guardar banco:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar banco');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (banco) => {
    setEditingBanco(banco);
    setFormData({
      codigo: banco.codigo || '',
      nombre: banco.nombre || '',
      nombre_corto: banco.nombre_corto || '',
      activo: banco.activo !== undefined ? banco.activo : true
    });
    setShowModal(true);
  };

  const handleDelete = async (bancoId) => {
    if (!window.confirm('¿Está seguro de eliminar este banco?')) {
      return;
    }

    try {
      await bancosApi.deleteBanco(bancoId);
      toast.success('Banco eliminado correctamente');
      loadBancos();
    } catch (error) {
      console.error('Error al eliminar banco:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar banco');
    }
  };

  const filteredBancos = bancos.filter(banco => {
    const search = searchTerm.toLowerCase();
    return (
      banco.codigo?.toLowerCase().includes(search) ||
      banco.nombre?.toLowerCase().includes(search) ||
      banco.nombre_corto?.toLowerCase().includes(search)
    );
  });

  return (
    <Container fluid className="p-4">
      <ToastContainer />
      <style>
        {`
          .dh-card-main {
            border-radius: 16px;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
            border: none;
          }
          .dh-card-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            padding: 1.5rem;
          }
          .dh-header-title {
            font-weight: 600;
            color: white;
          }
        `}
      </style>

      <Row>
        <Col>
          <Card className="dh-card-main mb-4">
            <Card.Header className="dh-card-header">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <FaBuilding className="me-3" size={28} />
                  <h3 className="dh-header-title mb-0">Mantenimiento de Bancos</h3>
                </div>
                <Button
                  variant="light"
                  onClick={() => {
                    resetForm();
                    setShowModal(true);
                  }}
                >
                  <FaPlus className="me-2" />
                  Nuevo Banco
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {/* Búsqueda */}
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>
                      <FaSearch className="me-2" />
                      Buscar
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Buscar por código, nombre o nombre corto..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Tabla de bancos */}
              {loading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Cargando bancos...</p>
                </div>
              ) : filteredBancos.length === 0 ? (
                <Alert variant="info">No se encontraron bancos</Alert>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Código</th>
                      <th>Nombre</th>
                      <th>Nombre Corto</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBancos.map(banco => (
                      <tr key={banco.id}>
                        <td>{banco.id}</td>
                        <td>{banco.codigo}</td>
                        <td>{banco.nombre}</td>
                        <td>{banco.nombre_corto || '-'}</td>
                        <td>
                          {banco.activo ? (
                            <Badge bg="success">Activo</Badge>
                          ) : (
                            <Badge bg="secondary">Inactivo</Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleEdit(banco)}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDelete(banco.id)}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal para crear/editar banco */}
      <Modal show={showModal} onHide={() => {
        setShowModal(false);
        resetForm();
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingBanco ? 'Editar Banco' : 'Nuevo Banco'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Código *</Form.Label>
                  <Form.Control
                    type="text"
                    name="codigo"
                    value={formData.codigo}
                    onChange={handleInputChange}
                    required
                    maxLength={20}
                    placeholder="Ej: BCP, BBVA"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre *</Form.Label>
                  <Form.Control
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    required
                    maxLength={255}
                    placeholder="Nombre completo del banco"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre Corto</Form.Label>
                  <Form.Control
                    type="text"
                    name="nombre_corto"
                    value={formData.nombre_corto}
                    onChange={handleInputChange}
                    maxLength={100}
                    placeholder="Nombre abreviado (opcional)"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="activo"
                    checked={formData.activo}
                    onChange={handleInputChange}
                    label="Activo"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowModal(false);
              resetForm();
            }}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : (editingBanco ? 'Actualizar' : 'Crear')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default MantenimientoBancos;
